import { useEffect, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { useCurrentDate } from '../../../hooks/useCurrentDate'
import { fileTypeToUserFacingName, numBytesToUserFacingSize } from '../../../utils/files'
import { Icon } from '../../Icon'
import Button from '../../app/design/Button'
import Label from '../../app/design/Label'

type Props = {
    name: string
    type?: string
    size?: number
    expirationDate?: Date | null
    imageClassName?: string
    onEditExpirationDate?: () => void
    onDelete?: () => void
} & ({ previewFile: File } | { previewURL: string })

export const UploadedImageFile = ({
    name,
    type,
    size,
    expirationDate,
    imageClassName,
    onEditExpirationDate,
    onDelete,
    ...props
}: Props) => {
    const currentDate = useCurrentDate()
    const isExpirationDateInPast = !!expirationDate && expirationDate < currentDate

    const [imagePreviewURL, setImagePreviewURL] = useState<string | null>(
        'previewURL' in props ? props.previewURL : null
    )

    const previewFile = 'previewFile' in props ? props.previewFile : undefined
    useEffect(() => {
        if (previewFile === undefined) {
            return
        }

        const reader = new FileReader()
        reader.onload = () => {
            setImagePreviewURL(reader.result as string)
        }
        reader.readAsDataURL(previewFile)
    }, [previewFile])

    return (
        <div className="flex items-stretch justify-center gap-x-4">
            <div
                className={twm(
                    'flex size-[108px] shrink-0 items-center justify-center rounded-lg',
                    imagePreviewURL === null && 'bg-gray-100',
                    imageClassName
                )}
            >
                {imagePreviewURL !== null ? (
                    <img
                        src={imagePreviewURL}
                        alt=""
                        className="size-full rounded-lg object-cover"
                    />
                ) : (
                    <Icon icon="file.large" />
                )}
            </div>

            <div className="flex min-w-0 shrink flex-col items-stretch justify-center gap-y-2 truncate">
                <div>
                    <Label className="block truncate" size="sm">
                        {name}
                    </Label>
                    {(type !== undefined || size !== undefined) && (
                        <div className="mt-1 flex items-center justify-start gap-x-2">
                            {type !== undefined && (
                                <Label size="sm" className="font-light text-gray-600">
                                    {fileTypeToUserFacingName(type)}
                                </Label>
                            )}

                            {type !== undefined && size !== undefined && (
                                <div className="size-0.5 rounded-full bg-gray-600" />
                            )}

                            {size !== undefined && (
                                <Label size="sm" className="font-light text-gray-600">
                                    {numBytesToUserFacingSize(size)}
                                </Label>
                            )}
                        </div>
                    )}
                </div>

                {expirationDate !== undefined &&
                    (expirationDate === null ? (
                        <Button intent="secondary" size="small" onClick={onEditExpirationDate}>
                            Add expiration date
                        </Button>
                    ) : (
                        <>
                            <div className="h-px w-full bg-gray-300" />

                            <div className="flex items-center justify-start gap-x-2">
                                <div
                                    className={twm(
                                        'flex size-4 items-center justify-center rounded-full border',
                                        isExpirationDateInPast
                                            ? 'border-danger-400'
                                            : 'border-success-400'
                                    )}
                                >
                                    {isExpirationDateInPast ? (
                                        <Icon
                                            icon="close.small"
                                            className="text-danger-400 size-3"
                                        />
                                    ) : (
                                        <Icon
                                            icon="checkmark.small"
                                            className="text-success-400 size-3"
                                        />
                                    )}
                                </div>

                                <Label size="sm" className="font-light text-gray-600">
                                    {isExpirationDateInPast ? 'Expired' : 'Expires'}{' '}
                                    {expirationDate.toLocaleDateString()}
                                </Label>

                                <Button
                                    intent="tertiary_deprecated"
                                    size="small"
                                    onClick={onEditExpirationDate}
                                    className="h-5 p-0"
                                >
                                    Edit
                                </Button>
                            </div>
                        </>
                    ))}
            </div>

            {onDelete && (
                <button className="shrink-0" onClick={onDelete}>
                    <Icon icon="trash.medium" className="text-gray-500" />
                </button>
            )}
        </div>
    )
}
